import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./auth";
import { DEFAULT_CONFIG, sectionKeys } from "./sectionRegistry";

const SECTIONS_PATH = "home.sections.v1";
const LiveEditCtx = createContext(null);

function normalizeConfig(raw) {
  const cfg = { ...DEFAULT_CONFIG, ...(raw || {}) };
  const valid = new Set(sectionKeys());
  const seen = new Set();
  cfg.order = (cfg.order || []).filter((k) => valid.has(k) && !seen.has(k) && seen.add(k));
  // Append any registered sections that aren't in the saved order at the end (hidden)
  const hidden = new Set(cfg.hidden || []);
  cfg.hidden = [...hidden].filter((k) => valid.has(k));
  return cfg;
}

export function LiveEditProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = !!user;
  const [editMode, setEditMode] = useState(false);
  const [content, setContent] = useState({}); // path -> {value,type,updated_at}
  const [sections, setSections] = useState(DEFAULT_CONFIG);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/page-content").then((r) => {
      if (!alive) return;
      const map = {};
      let parsed = null;
      (r.data || []).forEach((item) => {
        map[item.path] = item;
        if (item.path === SECTIONS_PATH) {
          try { parsed = JSON.parse(item.value); } catch {}
        }
      });
      setContent(map);
      setSections(normalizeConfig(parsed));
      setReady(true);
    }).catch(() => setReady(true));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!isAdmin) setEditMode(false);
  }, [isAdmin]);

  const get = useCallback((path, def) => {
    const v = content[path]?.value;
    return v === undefined || v === null || v === "" ? def : v;
  }, [content]);

  const save = useCallback(async (path, type, value) => {
    const res = await api.put(
      `/page-content/${path.split("/").map(encodeURIComponent).join("/")}`,
      { type, value }
    );
    setContent((c) => ({ ...c, [path]: res.data }));
    return res.data;
  }, []);

  const saveSections = useCallback(async (next) => {
    const clean = normalizeConfig(next);
    setSections(clean);
    try {
      await api.put(
        `/page-content/${encodeURIComponent(SECTIONS_PATH)}`,
        { type: "json", value: JSON.stringify(clean) }
      );
    } catch (e) {
      // Silent — non-admin visitors will hit 401 and that's expected
    }
  }, []);

  const hideSection = useCallback((key) => {
    saveSections({
      order: sections.order.filter((k) => k !== key),
      hidden: [...new Set([...(sections.hidden || []), key])],
    });
  }, [sections, saveSections]);

  const showSection = useCallback((key, atIndex) => {
    const newHidden = (sections.hidden || []).filter((k) => k !== key);
    const newOrder = [...sections.order];
    const idx = atIndex === undefined ? newOrder.length : atIndex;
    newOrder.splice(idx, 0, key);
    saveSections({ order: newOrder, hidden: newHidden });
  }, [sections, saveSections]);

  const moveSection = useCallback((key, dir) => {
    const idx = sections.order.indexOf(key);
    if (idx < 0) return;
    const to = idx + dir;
    if (to < 0 || to >= sections.order.length) return;
    const next = [...sections.order];
    [next[idx], next[to]] = [next[to], next[idx]];
    saveSections({ ...sections, order: next });
  }, [sections, saveSections]);

  const resetSections = useCallback(() => {
    saveSections(DEFAULT_CONFIG);
  }, [saveSections]);

  const value = useMemo(() => ({
    isAdmin, editMode, setEditMode,
    get, save, ready,
    sections,
    hideSection, showSection, moveSection, resetSections,
  }), [isAdmin, editMode, get, save, ready, sections, hideSection, showSection, moveSection, resetSections]);

  return <LiveEditCtx.Provider value={value}>{children}</LiveEditCtx.Provider>;
}

export const useLiveEdit = () =>
  useContext(LiveEditCtx) || {
    isAdmin: false, editMode: false, setEditMode: () => {},
    get: (_p, d) => d, save: async () => {},
    ready: true, sections: DEFAULT_CONFIG,
    hideSection: () => {}, showSection: () => {}, moveSection: () => {}, resetSections: () => {},
  };
